import React, { FC, useState, useEffect } from 'react';
import { Box, CircularProgress, ImageList, ImageListItem, Typography } from '@mui/material';
import { ScannerCase, ScannerCaseFileType } from 'models/scanner-case';
import Cookies from 'js-cookie';
import { CONSTANTS } from 'config/constants';

type DsCorePreviewProps = {
    scannerCase: ScannerCase;
    height?: number;
    cols?: number;
};
const DsCorePreview: FC<DsCorePreviewProps> = ({ scannerCase, height, cols }: DsCorePreviewProps) => {
    const [objectUrls, setObjectUrls] = useState<Record<string, string>>({});
    const [previewImages, setPreviewImages] = useState<{ fileSourceId: string; fileName: string; url: string }[]>([]);

    const boxStyle = {
        height: height ? `${height}px` : '200px',
        border: '1px #bbb solid',
        background: 'rgba(250,250,250,1)',
    };

    useEffect(() => {
        if (!scannerCase?.sourceFiles) {
            setPreviewImages([]);
            return;
        }
        const images = scannerCase.sourceFiles
            .filter((f) => f.type === ScannerCaseFileType.SCAN)
            .map((image) => ({
                fileSourceId: image.fileSourceId,
                fileName: image.fileName,
                url: `${CONSTANTS.DOC_API_URI}/ds-core-download-proxy/cases/${scannerCase.caseId}/preview/${image.fileSourceId}`,
            }));

        setPreviewImages(images);
    }, [scannerCase]);

    useEffect(() => {
        let isMounted = true;
        Object.values(objectUrls).forEach((url) => URL.revokeObjectURL(url));
        setObjectUrls({});

        const fetchImages = async () => {
            const newObjectUrls: Record<string, string> = {};
            await Promise.all(
                previewImages.map(async (img) => {
                    try {
                        const res = await fetch(img.url, {
                            headers: {
                                authorization: Cookies.get(CONSTANTS.TOKEN_KEY) || '',
                            },
                        });
                        if (res.ok) {
                            const blob = await res.blob();
                            newObjectUrls[img.fileSourceId] = URL.createObjectURL(blob);
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }),
            );
            if (isMounted) setObjectUrls(newObjectUrls);
        };
        fetchImages();
        return () => {
            isMounted = false;
            // Revoke object URLs on unmount/cleanup
            Object.values(objectUrls).forEach((url) => URL.revokeObjectURL(url));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewImages]);

    return (
        <ImageList cols={cols ? cols : previewImages.length} sx={{ m: 0 }}>
            {previewImages.map((img) => (
                <ImageListItem key={img.fileSourceId}>
                    {!height || height > 160 ? (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 1,
                                left: 1,
                                padding: '4px 8px',
                                backgroundColor: 'rgba(255,255,255,0.5)',
                                color: '#000',
                            }}
                        >
                            <Typography sx={{ padding: 0 }} variant="body2">
                                {img.fileName}
                            </Typography>
                        </Box>
                    ) : undefined}
                    {objectUrls[img.fileSourceId] ? (
                        <img style={boxStyle} src={objectUrls[img.fileSourceId] || ''} />
                    ) : (
                        <Box sx={boxStyle} display="flex" justifyContent="center" alignItems="center">
                            <CircularProgress />
                        </Box>
                    )}
                </ImageListItem>
            ))}
        </ImageList>
    );
};

export default DsCorePreview;
